import type { NgtAvailabilityResponseYes } from '~/components/routes/internet/internet.api';
import type { SingleInternetCustomerAddress } from '~/components/routes/internet/useInternetStore';
import config from '../../config';
import type { CustomerType } from '../../models/CustomerType';

interface CustomerVerificationDetails {
  provider: string;
  /** Opaque handle to the identity the api verified at callback and holds for us. */
  handle: string;
}

/** Machine readable reasons the api can reject a checkout, so the ui can react to each. */
export type CheckoutErrorCode =
  /** Verification has expired or been used. The customer has to verify again. */
  | 'verification_expired'
  /** We could not reach the verification store. Nothing is wrong on the customer's side. */
  | 'verification_unavailable'
  /** Verified identity does not match the submitted form. */
  | 'verification_failed'
  | 'unknown';

export class CheckoutError extends Error {
  readonly code: CheckoutErrorCode;

  constructor(code: CheckoutErrorCode) {
    super(`Checkout failed: ${code}`);
    this.name = 'CheckoutError';
    this.code = code;
  }
}

export interface CheckoutProduct {
  commercialProductId: number;
  offerId: number;
  contractPeriodId: number;
  channelIds: number[];
}

export interface CheckoutRequest {
  products: CheckoutProduct[];
  potentialCustomer: {
    firstName: string;
    lastName: string;
    email: string;
    mobilePhone: string;
    birthDate: string;
  };
  defaultAddress: {
    street: string;
    houseNo: string | undefined;
    houseNoAlpha: string | undefined;
    zip: string;
    city: string;
  };
  trackingCode: string;
  partnerAlias: string;
  isLaterActivation: boolean;
  laterActivationDate: string | undefined;
  customerType: CustomerType;
  customerVerificationDetails: CustomerVerificationDetails;

  internetServiceOrder?: {
    partnerId: number;
    installationAddress: SingleInternetCustomerAddress;
    product: {
      productTemplateId: string;
      productId: string;
      productName: string;
      productPrice: number;
    };
    availabilityInfo: NgtAvailabilityResponseYes['availabilityInfo'] & {
      installationEffort: string;
      deliveryStatus: string;
    };
  };
}

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const checkout = async (request: CheckoutRequest): Promise<string> => {
  const res = await fetch(`${config.apiUrl}/checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new CheckoutError(await readErrorCode(res));
  }
  return res.json();
};

const readErrorCode = async (res: Response): Promise<CheckoutErrorCode> => {
  try {
    const body = await res.json();
    return body?.code ?? 'unknown';
  } catch {
    // Older api versions answer with a plain string body.
    return 'unknown';
  }
};
